<template>
  <div>
    <v-row>
      <v-col cols="12">
        <h1 class="text-h4 mb-1">Plant Database Dashboard</h1>
      </v-col>
    </v-row>

    <!-- Stats row -->
    <v-row>
      <v-col cols="6" sm="4" md="2">
        <v-card variant="tonal" color="primary" to="/plants" hover class="clickable-stat">
          <v-card-text class="text-center pa-3">
            <div class="text-h4 font-weight-bold">{{ stats.total_plants }}</div>
            <div class="text-caption">Total Plants</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card variant="tonal" color="success" :to="{ path: '/plants', query: { is_native_fl: 'true' } }" hover class="clickable-stat">
          <v-card-text class="text-center pa-3">
            <div class="text-h4 font-weight-bold">{{ stats.native_count }}</div>
            <div class="text-caption">Native Plants</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card variant="tonal" color="info" to="/images" hover class="clickable-stat">
          <v-card-text class="text-center pa-3">
            <div class="text-h4 font-weight-bold">{{ stats.with_photos }}</div>
            <div class="text-caption">With Photos</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card variant="tonal" color="warning" :to="{ path: '/plants', query: { data_status: 'no_plan_color' } }" hover class="clickable-stat">
          <v-card-text class="text-center pa-3">
            <div class="text-h4 font-weight-bold">{{ stats.with_plan_colors }}</div>
            <div class="text-caption">With Plan Colors</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card variant="tonal" color="secondary" to="/palette-builder" hover class="clickable-stat">
          <v-card-text class="text-center pa-3">
            <div class="text-h4 font-weight-bold">{{ paletteCount }}</div>
            <div class="text-caption">Palettes</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="4" md="2">
        <v-card
          variant="tonal"
          :color="missingDataCount > 0 ? 'error' : 'success'"
          :to="missingDataCount > 0 ? '/needs-attention' : undefined"
          :hover="missingDataCount > 0"
          :class="{ 'clickable-stat': missingDataCount > 0 }"
        >
          <v-card-text class="text-center pa-3">
            <div class="text-h4 font-weight-bold">{{ missingDataCount }}</div>
            <div class="text-caption">Need Attention</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Primary actions -->
    <v-row class="mt-2">
      <v-col cols="12" sm="6" md="3" v-for="action in primaryActions" :key="action.to">
        <v-card
          :to="action.to"
          hover
          class="primary-action-card"
          height="120"
        >
          <v-card-text class="d-flex flex-column justify-center align-center h-100">
            <v-icon size="32" :color="action.color" class="mb-2">{{ action.icon }}</v-icon>
            <div class="text-subtitle-1 font-weight-medium">{{ action.title }}</div>
            <div class="text-caption text-medium-emphasis">{{ action.desc }}</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Category breakdown - chip grid -->
    <v-row class="mt-2">
      <v-col cols="12">
        <v-card>
          <v-card-title class="text-subtitle-1 pb-1">
            <v-icon start size="small">mdi-chart-pie</v-icon>
            Plants by Category
          </v-card-title>
          <v-card-text v-if="categoryList.length === 0" class="text-center text-medium-emphasis py-4">
            Loading...
          </v-card-text>
          <v-card-text v-else class="d-flex flex-wrap ga-2 pt-0">
            <v-chip
              v-for="cat in categoryList"
              :key="cat.name"
              :color="cat.color"
              variant="tonal"
              size="small"
              :to="{ path: '/plants', query: { search: cat.name } }"
              class="clickable-chip"
            >
              {{ cat.name }}
              <span class="ml-1 font-weight-bold">{{ cat.count }}</span>
              <span class="text-medium-emphasis ml-1">({{ cat.pct }}%)</span>
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Families + Data Gaps side by side -->
    <v-row class="mt-2">
      <v-col cols="12" md="7">
        <v-card>
          <v-card-title class="text-subtitle-1 pb-1">
            <v-icon start size="small">mdi-family-tree</v-icon>
            Top Families
          </v-card-title>
          <v-card-text v-if="familyList.length === 0" class="text-center text-medium-emphasis py-4">
            Loading...
          </v-card-text>
          <v-card-text v-else class="d-flex flex-wrap ga-2 pt-0">
            <v-chip
              v-for="fam in familyList"
              :key="fam.name"
              variant="tonal"
              color="primary"
              size="small"
              :to="{ path: '/plants', query: { family: fam.name } }"
              class="clickable-chip"
            >
              {{ fam.name }} <span class="ml-1 font-weight-bold">{{ fam.count }}</span>
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card>
          <v-card-title class="text-subtitle-1 pb-1">
            <v-icon start size="small">mdi-alert-circle-outline</v-icon>
            Data Gaps
          </v-card-title>
          <v-card-text v-if="!missingData" class="text-center text-medium-emphasis py-4">
            Loading...
          </v-card-text>
          <v-card-text v-else class="d-flex flex-wrap ga-2 pt-0">
            <v-chip
              v-for="gap in dataGaps"
              :key="gap.key"
              :color="gap.count > 0 ? 'warning' : 'success'"
              variant="tonal"
              size="small"
              :to="gap.count > 0 ? (gap.key === 'no_photo' ? '/image-finder' : { path: '/needs-attention', query: { gap: gap.key } }) : undefined"
              :class="{ 'clickable-chip': gap.count > 0 }"
            >
              <v-icon start size="x-small">{{ gap.count > 0 ? 'mdi-alert' : 'mdi-check-circle' }}</v-icon>
              {{ gap.label }} <span class="ml-1 font-weight-bold">{{ gap.count }}</span>
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Tool groups -->
    <v-row class="mt-2">
      <v-col cols="12" md="4" v-for="group in toolGroups" :key="group.title">
        <v-card>
          <v-card-title class="text-subtitle-1">
            <v-icon start size="small">{{ group.icon }}</v-icon>
            {{ group.title }}
          </v-card-title>
          <v-card-text class="pt-0">
            <v-chip
              v-for="tool in group.tools"
              :key="tool.to"
              :to="tool.to"
              :prepend-icon="tool.icon"
              variant="tonal"
              :color="group.color"
              class="mr-2 mb-2"
              size="small"
            >
              {{ tool.label }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { getPlants } from '@/api/endpoints/plants/plants'
import { getPalettes } from '@/api/endpoints/palettes/palettes'

const { plantsStatsRetrieve, plantsMissingDataRetrieve } = getPlants()
const { palettesList } = getPalettes()

const stats = ref({
  total_plants: 0,
  native_count: 0,
  with_photos: 0,
  with_plan_colors: 0,
  by_category: {} as Record<string, number>,
  by_family: {} as Record<string, number>,
})

const missingData = ref<Record<string, any[]> | null>(null)
const paletteCount = ref(0)

const categoryColors = ['primary', 'success', 'info', 'teal', 'orange', 'purple', 'cyan', 'pink', 'amber', 'indigo']

const categoryList = computed(() => {
  const entries = Object.entries(stats.value.by_category)
  if (entries.length === 0) return []
  const total = entries.reduce((s, [, c]) => s + c, 0)
  return entries
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([name, count], i) => ({
      name,
      count,
      pct: total > 0 ? Math.round((count / total) * 100) : 0,
      color: categoryColors[i % categoryColors.length],
    }))
})

const familyList = computed(() => {
  return Object.entries(stats.value.by_family)
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({ name, count }))
})

const dataGaps = computed(() => {
  if (!missingData.value) return []
  return [
    { key: 'no_common_name', label: 'Missing common name', count: missingData.value.no_common_name?.length || 0 },
    { key: 'no_category', label: 'Missing category', count: missingData.value.no_category?.length || 0 },
    { key: 'no_photo', label: 'Missing photo', count: missingData.value.no_photo?.length || 0 },
    { key: 'no_plan_color', label: 'Missing plan color', count: missingData.value.no_plan_color?.length || 0 },
    { key: 'no_plan_diameter', label: 'Missing plan diameter', count: missingData.value.no_plan_diameter?.length || 0 },
  ]
})

const missingDataCount = computed(() => {
  return dataGaps.value.reduce((sum, g) => sum + g.count, 0)
})

const primaryActions = [
  { to: '/plants', icon: 'mdi-flower', color: 'primary', title: 'Browse Plants', desc: 'View and edit all plants' },
  { to: '/palette-builder', icon: 'mdi-palette-swatch', color: 'purple', title: 'Palette Builder', desc: 'Create plant palettes' },
  { to: '/data-workflow', icon: 'mdi-database-edit', color: 'info', title: 'Data Workflow', desc: 'Edit plant details' },
  { to: '/site-plan', icon: 'mdi-floor-plan', color: 'teal', title: 'Site Plan Generator', desc: 'Generate site plans' },
]

const toolGroups = [
  {
    title: 'Data Tools',
    icon: 'mdi-database-cog',
    color: 'info',
    tools: [
      { label: 'Import', icon: 'mdi-upload', to: '/import' },
      { label: 'Bulk Editor', icon: 'mdi-table-edit', to: '/bulk-editor' },
      { label: 'Bulk Import', icon: 'mdi-file-import', to: '/bulk-import' },
      { label: 'Bulk Scraping', icon: 'mdi-web-sync', to: '/bulk-scraping' },
      { label: 'Reconcile', icon: 'mdi-scale-balance', to: '/reconciliation' },
      { label: 'Needs Attention', icon: 'mdi-alert-circle-check', to: '/needs-attention' },
    ],
  },
  {
    title: 'Content',
    icon: 'mdi-image-multiple',
    color: 'success',
    tools: [
      { label: 'Image Finder', icon: 'mdi-image-search', to: '/image-finder' },
      { label: 'Images', icon: 'mdi-image-multiple', to: '/images' },
      { label: 'Artwork Manager', icon: 'mdi-vector-square', to: '/artwork-manager' },
      { label: 'Source URLs', icon: 'mdi-link-variant', to: '/source-urls' },
      { label: 'Cultivars', icon: 'mdi-sprout', to: '/cultivars' },
      { label: 'Nursery Pricing', icon: 'mdi-currency-usd', to: '/nursery-pricing' },
    ],
  },
  {
    title: 'Analysis',
    icon: 'mdi-chart-box-outline',
    color: 'warning',
    tools: [
      { label: 'Soil Compatibility', icon: 'mdi-land-rows-horizontal', to: '/soil-compatibility' },
      { label: 'Add Plant', icon: 'mdi-leaf-maple', to: '/plants/create-new' },
      { label: 'Scheduled Deletions', icon: 'mdi-delete-clock', to: '/scheduled-deletions' },
    ],
  },
]

onMounted(async () => {
  const [statsData, missingDataRes, palettesData] = await Promise.all([
    plantsStatsRetrieve(),
    plantsMissingDataRetrieve(),
    palettesList(),
  ])
  stats.value = statsData as any
  missingData.value = missingDataRes as any
  const palettes = (palettesData as any).results || palettesData
  paletteCount.value = Array.isArray(palettes) ? palettes.length : 0
})
</script>

<style scoped>
.primary-action-card {
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.primary-action-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
.clickable-chip {
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}
.clickable-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.clickable-stat {
  cursor: pointer;
}
</style>
